// Shared engraving validation for every product form, including sticky submissions.
(() => {
  if (window.StellarEngraving) return;
  const emoji = /[\p{Extended_Pictographic}\p{Regional_Indicator}\p{Emoji_Modifier}\u200D\u20E3\uFE0E\uFE0F]/u;
  const emoticon = /(?:[:;=][\-^']?[)(DPp/\\]|\^[_-]?\^|<3)/;
  const invalidUnicode = /[\uD800-\uDFFF\u0000-\u001F\u007F]/u;
  // Exact verified Autography asset SHA256: 8d3162f06d78bf817c8f0cd565bff8a6cdb849a1b7eddc4b9b72c52dfde26a47
  const autographyRanges = [[32,126],[161,163],[165,165],[167,169],[171,171],[174,174],[177,181],[184,185],[187,187],[191,221],[223,239],[241,246],[248,253],[255,255],[305,305],[338,339],[352,353],[376,376],[381,382],[710,711],[730,733],[8211,8212],[8216,8217],[8220,8221],[8224,8226],[8240,8240],[8249,8250],[8364,8364],[8482,8482],[57344,57391],[57893,57893],[57909,57909],[57916,57916],[58017,58017],[58020,58020],[58023,58024],[58026,58031],[58035,58036],[58086,58086],[58104,58104],[58520,58520],[58561,58561],[58565,58565],[58567,58567],[58570,58571],[58578,58579],[63168,63168],[64256,64256]];
  const error = (value, font = '') => {
    if (value.length > 22) return 'Please use no more than 22 characters, including spaces.';
    if (emoji.test(value) || emoticon.test(value)) return 'Please remove emoji or emoticons. Use letters, numbers, spaces and punctuation.';
    if (invalidUnicode.test(value)) return 'This text contains an unsupported character. Please remove it before adding personalisation.';
    if (font === 'autography' && [...value.normalize('NFC')].some(character => {
      const code = character.codePointAt(0);
      return !autographyRanges.some(([first, last]) => code >= first && code <= last);
    })) return 'This font does not support one or more characters in your text. Please use supported characters or contact us.';
    return '';
  };
  const fieldFor = form => form?.querySelector('[name="properties[Personalisation]"]');
  const selected = form => {
    const choice = form?.querySelector('.product-personalisation-wrap input[type="checkbox"]');
    return !!choice?.checked && !choice.disabled;
  };
  const fontFor = form => {
    if (!form) return '';
    const preview = [...document.querySelectorAll('stellar-engraving-preview')].find(item => item.dataset.formId === form.id);
    if (!preview) return '';
    let scope = preview.querySelector('[data-engraving-artwork]');
    if (preview.hasAttribute('data-variant-preview')) {
      const id = form.querySelector('[name="id"]')?.value;
      scope = [...preview.querySelectorAll('template[data-engraving-variant]')].find(item => item.dataset.engravingVariant === id)?.content;
    }
    const artwork = scope?.querySelector('[data-font-source]');
    return artwork?.dataset.fontSource === 'asset' && /(?:^|\/)autography\.woff2(?:\?|$)/.test(artwork.dataset.fontUrl || '') ? 'autography' : '';
  };
  const reflect = (form, focus = false) => {
    const field = fieldFor(form);
    if (!field) return '';
    let counter = form.querySelector('[data-personalisation-counter]');
    if (!counter) {
      counter = document.createElement('small');
      counter.dataset.personalisationCounter = '';
      counter.id = (field.id || form.id || 'Personalisation') + '-count';
      counter.setAttribute('aria-live', 'polite');
      counter.setAttribute('aria-atomic', 'true');
      field.after(counter);
      const described = new Set((field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      described.add(counter.id);
      field.setAttribute('aria-describedby', [...described].join(' '));
    }
    const count = `${field.value.length} / 22 characters`;
    if (counter.textContent !== count) counter.textContent = count;
    const message = selected(form) ? error(field.value, fontFor(form)) : '';
    field.setCustomValidity(message);
    field.toggleAttribute('aria-invalid', !!message);
    if (message) field.setAttribute('aria-invalid', 'true');
    const note = form.querySelector('.personalisation-error');
    if (note) {
      if (!note.id) note.id = (field.id || form.id || 'Personalisation') + '-validation';
      note.setAttribute('role', 'status');
      note.setAttribute('aria-live', 'polite');
      const described = new Set((field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      described.add(note.id);
      field.setAttribute('aria-describedby', [...described].join(' '));
      if (message || note.dataset.engravingValidation === 'true') note.textContent = message;
      note.dataset.engravingValidation = String(!!message);
    }
    if (message && focus) {
      const details = field.closest('details');
      if (details) details.open = true;
      const container = field.closest('.personalisation-value');
      if (container) { container.hidden = false; container.classList.add('active'); container.style.display = 'block'; }
      field.focus();
    }
    return message;
  };
  window.StellarEngraving = {error, reflect, fontFor, maxLength: 22};
  const composingFields = new WeakSet();
  document.addEventListener('compositionstart', event => {
    if (event.target.matches?.('[name="properties[Personalisation]"]')) composingFields.add(event.target);
  });
  for (const type of ['input', 'change', 'compositionend']) {
    document.addEventListener(type, event => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.matches('[name="properties[Personalisation]"]')) {
        if (type === 'compositionend') composingFields.delete(target);
        if (!event.isComposing && !composingFields.has(target) && target.value.length > 22) {
          const start = target.selectionStart, end = target.selectionEnd;
          target.value = target.value.slice(0, 22);
          if (start !== null) target.setSelectionRange(Math.min(start, 22), Math.min(end, 22));
        }
      }
      if (target.matches('[name="properties[Personalisation]"], .product-personalisation-wrap input[type="checkbox"]')) reflect(target.closest('form'));
    });
  }
  document.addEventListener('invalid', event => {
    if (event.target.matches?.('[name="properties[Personalisation]"]') && error(event.target.value, fontFor(event.target.closest('form')))) reflect(event.target.closest('form'), true);
  }, true);
  document.addEventListener('submit', event => {
    if (reflect(event.target, true)) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('stellar:purchase-state', event => reflect(event.target.closest?.('form') || event.target));
  document.addEventListener('stellar:product-form-ready', event => reflect(event.target.querySelector('form')));
  document.querySelectorAll('form').forEach(form => reflect(form));
})();

if (!customElements.get('product-form')) {
  const cartReviewKey = 'stellar-cart-review-required';
  const cartUrl = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
  const cartAddState = { pending: false, review: false };
  try {
    const cartPath = new URL(cartUrl, window.location.href).pathname.replace(/\/$/, '');
    if (window.location.pathname.replace(/\/$/, '') === cartPath) sessionStorage.removeItem(cartReviewKey);
    else cartAddState.review = sessionStorage.getItem(cartReviewKey) === '1';
  } catch (_) {}
  const persistCartReview = required => {
    try { if (required) sessionStorage.setItem(cartReviewKey, '1'); else sessionStorage.removeItem(cartReviewKey); } catch (_) {}
  };
  window.addEventListener('pageshow', event => {
    if (!event.persisted || cartAddState.pending) return;
    try {
      const review = sessionStorage.getItem(cartReviewKey) === '1';
      if (review === cartAddState.review) return;
      cartAddState.review = review;
      document.querySelectorAll('product-form').forEach(form => form.restoreCartAddState());
    } catch (_) {}
  });
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      if (!this.form) return;
      const variant = this.form.querySelector('[name=id]');
      if (variant) variant.disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.hideErrors = this.dataset.hideErrors === 'true';
      this.submitting = false;
    }
    connectedCallback() {
      this.dispatchEvent(new CustomEvent('stellar:product-form-ready', { bubbles: true }));
      if (cartAddState.review && this.form) this.showCartReview();
    }
    onSubmitHandler(evt) {
      evt.preventDefault();
      const submitButton = evt.submitter || this.form.querySelector('[type="submit"]');
      if (!submitButton || submitButton.disabled) return;
      if (cartAddState.review) { delete submitButton.dataset.stellarCheckout; this.showCartReview(); return; }
      if (cartAddState.pending || this.submitting || submitButton.getAttribute('aria-disabled') === 'true') return;
      const text = window.stellarStrings || {};
      const directCheckout = submitButton.dataset.stellarCheckout === 'true';
      delete submitButton.dataset.stellarCheckout;
      const source = new FormData(this.form);
      const mainProductId = source.get('id');
      const quantity = Number(source.get('quantity') || 1);
      if (!mainProductId || !Number.isInteger(quantity) || quantity < 1) {
        this.handleErrorMessage(text.checkSelection); return;
      }
      const personal = this.form.querySelector('.product-personalisation-wrap input[type="checkbox"]');
      const wrap = this.form.querySelector('.product-gift-wrap .product-gift-checkbox');
      const personalField = this.form.querySelector('[name="properties[Personalisation]"]');
      const personalSelected = !!personal?.checked && !personal.disabled;
      const wrapSelected = !!wrap?.checked && !wrap.disabled;
      const selectedWrap = Array.from(this.form.querySelectorAll('#variant-options .variant-option')).find(option => option.dataset.variantId === wrap?.value);
      const wrapName = (selectedWrap?.dataset.variantName || wrap?.dataset.variantName || '').trim();
      if (wrapSelected && (!wrap.value || !wrapName)) { this.handleErrorMessage(text.checkSelection); return; }
      const personalError = this.form.querySelector('.personalisation-error');
      if (personalError) personalError.textContent = '';
      if (personalSelected && (!personalField?.value.trim() || personalField.value.length > 22)) {
        if (personalError) personalError.textContent = text.personalisationError;
        if (personalField) {
          const details = personalField.closest('details');
          if (details) details.open = true;
          personalField.closest('.personalisation-value')?.classList.add('active');
          personalField.setAttribute('aria-invalid', 'true'); personalField.focus();
        }
        this.handleErrorMessage(text.personalisationError); return;
      }
      if (personalSelected) {
        const engravingError = window.StellarEngraving ? window.StellarEngraving.reflect(this.form, true) : 'Personalisation validation is unavailable. Please reload this page before adding your engraving.';
        if (engravingError) { this.handleErrorMessage(engravingError); return; }
      }
      this.handleErrorMessage(); this.submitting = true; this.error = false;
      cartAddState.pending = true;
      persistCartReview(true);
      submitButton.setAttribute('aria-disabled', 'true'); submitButton.setAttribute('aria-busy', 'true');
      submitButton.classList.add('loading');
      const spinner = submitButton.querySelector('.loading-overlay__spinner');
      spinner?.classList.remove('hidden');
      const formData = new FormData();
      formData.append('items[0][id]', mainProductId); formData.append('items[0][quantity]', quantity);
      if (source.get('selling_plan')) formData.append('items[0][selling_plan]', source.get('selling_plan'));
      for (const [name, value] of source.entries()) {
        if (!name.startsWith('properties[')) continue;
        if (name === 'properties[Personalisation]' && personal) continue;
        if (name === 'properties[Gift Wrapping]' && wrap) continue;
        if (name === 'properties[Trade up]' && value === 'false') continue;
        if (typeof value === 'string' && !value.trim()) continue;
        formData.append(`items[0][properties][${name.slice(11, -1)}]`, value);
      }
      if (personalSelected) formData.append('items[0][properties][Personalisation]', personalField.value.trim());
      if (wrapSelected) formData.append('items[0][properties][Gift Wrapping]', wrapName);
      let itemIndex = 1;
      for (const addon of [wrapSelected ? wrap : null, personalSelected ? personal : null]) {
        if (!addon?.value) continue;
        formData.append(`items[${itemIndex}][id]`, addon.value); formData.append(`items[${itemIndex}][quantity]`, quantity);
        formData.append(`items[${itemIndex}][parent_id]`, mainProductId);
        itemIndex++;
      }
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest'; delete config.headers['Content-Type'];
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map(section => section.id));
        formData.append('sections_url', window.location.pathname); this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;
      this.cartRequest(routes.cart_add_url, config)
        .then(async ({ response, result }) => {
          if (!response.ok || result.status) {
            const message = result.description || result.message || text.addError;
            publish(PUB_SUB_EVENTS.cartError, { source: 'product-form', productVariantId: mainProductId, errors: message, message });
            await this.reconcileCartAddition(message); return;
          }
          persistCartReview(false);
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: mainProductId, cartRenderTarget: this.cart });
          if (directCheckout) { window.location.assign((window.Shopify?.routes?.root || '/') + 'checkout'); return; }
          if (!this.cart) { window.location.assign(window.routes.cart_url); return; }
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => this.cart.renderContents(result), { once: true }); quickAddModal.hide(true);
          } else this.cart.renderContents(result);
          this.cart.classList.remove('is-empty');
        })
        .catch(() => this.reconcileCartAddition(text.addError))
        .finally(() => {
          this.submitting = false; cartAddState.pending = false; submitButton.classList.remove('loading');
          if (!cartAddState.review) submitButton.removeAttribute('aria-disabled');
          submitButton.removeAttribute('aria-busy'); spinner?.classList.add('hidden');
          this.form.dispatchEvent(new CustomEvent('stellar:purchase-state', { bubbles: true }));
        });
    }
    async cartRequest(url, config = {}) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { ...config, signal: controller.signal, cache: 'no-store' });
        const result = await response.json();
        if (controller.signal.aborted) throw new Error('Cart request timed out');
        return { response, result };
      } finally { clearTimeout(timer); }
    }
    showCartReview(message = this.cartReviewMessage || '') {
      this.cartReviewMessage = message;
      const text = window.stellarStrings || {};
      let wrapper = this.querySelector('.product-form__error-message-wrapper');
      if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.className = 'product-form__error-message-wrapper';
        wrapper.setAttribute('role', 'alert');
        const content = document.createElement('span');
        content.className = 'product-form__error-message';
        wrapper.append(content); this.append(wrapper);
      }
      wrapper.hidden = false;
      const content = wrapper.querySelector('.product-form__error-message');
      if (content) content.textContent = [message, text.cartReviewRequired].filter(Boolean).join(' ');
      let link = wrapper.querySelector('[data-cart-review]');
      if (!link) { link = document.createElement('a'); link.setAttribute('data-cart-review', ''); wrapper.append(' ', link); }
      link.href = cartUrl; link.textContent = text.reviewCart;
      this.form.querySelectorAll('[type="submit"]').forEach(button => {
        button.setAttribute('data-stellar-cart-review', ''); button.setAttribute('aria-disabled', 'true');
      });
      this.form.dispatchEvent(new CustomEvent('stellar:purchase-state', { bubbles: true }));
    }
    restoreCartAddState() {
      if (cartAddState.review) { this.showCartReview(); return; }
      const buttons = this.form?.querySelectorAll('[data-stellar-cart-review]');
      if (!buttons?.length) return;
      buttons.forEach(button => { button.removeAttribute('data-stellar-cart-review'); button.removeAttribute('aria-disabled'); });
      const wrapper = this.querySelector('.product-form__error-message-wrapper');
      if (wrapper) wrapper.hidden = true;
      this.cartReviewMessage = '';
      this.form.dispatchEvent(new CustomEvent('stellar:purchase-state', { bubbles: true }));
    }
    async reconcileCartAddition(message) {
      cartAddState.review = true; persistCartReview(true);
      this.showCartReview(message);
      try {
        const { response, result: cartData } = await this.cartRequest(`${cartUrl}.js`);
        if (!response.ok || !Array.isArray(cartData.items)) return;
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData });
        document.querySelector('cart-items')?.onCartUpdate();
        if (!this.cart) return;
        const sectionUrl = new URL(cartUrl, window.location.href);
        const sectionsToRender = this.cart.getSectionsToRender();
        sectionUrl.searchParams.set('sections', sectionsToRender.map(section => section.id).join(','));
        const { response: sectionResponse, result: sections } = await this.cartRequest(sectionUrl.href);
        if (!sectionResponse.ok || sectionsToRender.some(section => typeof sections[section.id] !== 'string')) return;
        const render = () => {
          this.cart.classList.toggle('is-empty', cartData.item_count === 0);
          this.cart.renderContents({ ...cartData, sections });
        };
        const modal = this.closest('quick-add-modal');
        if (modal) { document.body.addEventListener('modalClosed', render, { once: true }); modal.hide(true); }
        else render();
      } catch (_) {}
    }
    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;
      const wrapper = this.querySelector('.product-form__error-message-wrapper');
      if (!wrapper) return;
      wrapper.toggleAttribute('hidden', !errorMessage);
      const message = wrapper.querySelector('.product-form__error-message');
      if (message && errorMessage) message.textContent = errorMessage;
    }
  });
}

